import {
  type APIMessage,
  can,
  composerState,
  PermissionFlags,
  formatDuration,
  formatMessageTime,
  isChatApiError,
  t,
} from '@alternativamais/chat-core';
import {
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { avisar } from '../../../componentes/Avisos';
import {
  CaixaMenu,
  Flutuante,
  ItemMenu,
  type Posicao,
} from '../../../componentes/Flutuante';
import { abrirCamada } from '../../../chat/camadas';
import { Icone } from '../../../componentes/Icone';
import { useAutoAltura } from '../../../componentes/useAutoAltura';
import { usePreferencias } from '../../../chat/preferencias';
import { useAtender } from '../../../chat/atalhos';
import { tocarSom } from '../../../chat/sons';
import { useAjusteDaConta } from '../../../chat/ganchos';
import { useCliente, useEstado, useEstadoRaso } from '../../../chat/contexto';
import { useNome } from '../../../chat/ganchos';
import {
  acharGatilho,
  type Gatilho,
  substituir,
} from '@alternativamais/chat-core';
import { carregarEmojis, mapaDeAtalhos } from '../../../utilidades/emojis';
import {
  comEmoticons,
  contextoDoCanal,
  paraEnvio,
} from '@alternativamais/chat-core';
import { SeletorEmoji } from '../SeletorEmoji';
import { type Sugestao, Sugestoes, useSugestoes } from './Sugestoes';

/** Limite do Discord por mensagem. */
const MAX_CARACTERES = 2000;
const MAX_ARQUIVOS = 10;
const MAX_BYTES = 50 * 1024 * 1024;

export type Resposta = { mensagem: APIMessage; mencionar: boolean };

/** O canal pede ao compositor: arquivos soltos na tela, foco, menção. */
export type ControleCompositor = {
  adicionarArquivos(arquivos: File[]): void;
  focar(): void;
  /** Põe um texto no fim (o "Mencionar" do menu do membro). */
  inserir(texto: string): void;
};

type Anexo = {
  id: string;
  arquivo: File;
  spoiler: boolean;
  previa: string | null;
};

let proximoAnexo = 1;

/**
 * Compositor (C4, C43): texto com rascunho por canal, resposta, anexos
 * (botão, colar e soltar), sugestões de @, # e :, emoji, "digitando",
 * modo lento e o motivo quando não dá para escrever.
 */
export function Compositor({
  canalId,
  guildId,
  nomeDoCanal,
  resposta,
  aoMudarResposta,
  aoEditarUltima,
  controle,
}: {
  canalId: string;
  guildId: string | null;
  /** "#canal" ou "@pessoa" no texto de exemplo. */
  nomeDoCanal: { tipo: 'canal' | 'dm'; nome: string; texto?: string };
  resposta: Resposta | null;
  aoMudarResposta: (resposta: Resposta | null) => void;
  aoEditarUltima: () => void;
  controle: RefObject<ControleCompositor | null>;
}) {
  const cliente = useCliente();
  const estado = useEstadoRaso((atual) => composerState(atual, canalId));
  const castigoAte = useEstado((atual) =>
    guildId && atual.me
      ? (atual.members[guildId]?.[atual.me.id]?.communication_disabled_until ??
        null)
      : null,
  );
  const [texto, setTexto] = useState(
    () => cliente.drafts.get(canalId)?.content ?? '',
  );
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [gatilho, setGatilho] = useState<Gatilho | null>(null);
  const [selecionada, setSelecionada] = useState(0);
  const [emojiAberto, setEmojiAberto] = useState<Posicao | null>(null);
  const [menuMais, setMenuMais] = useState<Posicao | null>(null);
  const podeEnquete = useEstado((atual) =>
    atual.channels[canalId]?.guild_id
      ? can(atual, canalId, PermissionFlags.SEND_POLLS)
      : true,
  );
  const [esperaAte, setEsperaAte] = useState<number | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  const escolhidas = useRef(new Map<string, string>());
  const campo = useRef<HTMLTextAreaElement>(null);
  const seletorArquivo = useRef<HTMLInputElement>(null);
  const formulario = useRef<HTMLFormElement>(null);
  const botaoEmoji = useRef<HTMLButtonElement>(null);
  const prefs = usePreferencias();
  const converter = useAjusteDaConta('convert_emoticons');
  // Atalhos (Ctrl+Shift+U, Ctrl+E) valem para o compositor focado ou o da
  // conversa principal (o primeiro da tela).
  const souOAlvo = () => {
    const focado = document.activeElement?.closest(
      'form[aria-label="Enviar mensagem"]',
    );
    const alvo =
      focado ?? document.querySelector('form[aria-label="Enviar mensagem"]');
    return alvo === formulario.current;
  };
  useAtender('enviar-arquivo', () => {
    if (souOAlvo()) seletorArquivo.current?.click();
  });
  useAtender('emojis', () => {
    if (!souOAlvo()) return;
    const caixa = botaoEmoji.current?.getBoundingClientRect();
    if (caixa) setEmojiAberto({ x: caixa.right, y: caixa.top - 8 });
  });
  const idSugestoes = useId();
  useAutoAltura(campo, texto, 360);

  // Rascunho: lê do armazenamento ao abrir; grava com espera ao digitar e
  // na hora ao sair (trocar de canal, recarregar, fechar a aba). Só grava
  // depois de ler: antes disso, o texto vazio apagaria o rascunho guardado.
  const rascunhoLido = useRef(false);
  const textoAtual = useRef(texto);
  useEffect(() => {
    textoAtual.current = texto;
  }, [texto]);
  useEffect(() => {
    let vivo = true;
    if (cliente.drafts.get(canalId)) {
      rascunhoLido.current = true;
      return;
    }
    void cliente.drafts.load(canalId).then((rascunho) => {
      if (!vivo) return;
      rascunhoLido.current = true;
      if (rascunho?.content) setTexto((atual) => atual || rascunho.content);
    });
    return () => {
      vivo = false;
    };
  }, [cliente, canalId]);
  useEffect(() => {
    const espera = window.setTimeout(() => {
      if (rascunhoLido.current) void cliente.drafts.set(canalId, texto);
    }, 400);
    return () => window.clearTimeout(espera);
  }, [cliente, canalId, texto]);
  useEffect(() => {
    const salvar = () => {
      if (rascunhoLido.current) {
        void cliente.drafts.set(canalId, textoAtual.current);
      }
    };
    window.addEventListener('pagehide', salvar);
    return () => {
      window.removeEventListener('pagehide', salvar);
      salvar();
    };
  }, [cliente, canalId]);

  // Anexos: soltar e colar vêm por aqui; as prévias são liberadas ao sair.
  const adicionarArquivos = useCallback((arquivos: File[]) => {
    const grandes = arquivos.filter((arquivo) => arquivo.size > MAX_BYTES);
    if (grandes.length) avisar(`${grandes[0].name} passa de 50 MB.`);
    const validos = arquivos.filter((arquivo) => arquivo.size <= MAX_BYTES);
    setAnexos((atuais) => {
      const vagas = MAX_ARQUIVOS - atuais.length;
      if (validos.length > vagas)
        avisar(`Até ${MAX_ARQUIVOS} arquivos por mensagem.`);
      return [
        ...atuais,
        ...validos.slice(0, Math.max(0, vagas)).map((arquivo) => ({
          id: String(proximoAnexo++),
          arquivo,
          spoiler: false,
          previa: arquivo.type.startsWith('image/')
            ? URL.createObjectURL(arquivo)
            : null,
        })),
      ];
    });
    campo.current?.focus();
  }, []);
  const anexosAtuais = useRef(anexos);
  useEffect(() => {
    anexosAtuais.current = anexos;
  }, [anexos]);
  useEffect(
    () => () => {
      for (const anexo of anexosAtuais.current)
        if (anexo.previa) URL.revokeObjectURL(anexo.previa);
    },
    [],
  );
  const removerAnexo = (id: string) =>
    setAnexos((atuais) => {
      const sai = atuais.find((anexo) => anexo.id === id);
      if (sai?.previa) URL.revokeObjectURL(sai.previa);
      return atuais.filter((anexo) => anexo.id !== id);
    });

  useImperativeHandle(
    controle,
    () => ({
      adicionarArquivos,
      focar: () => campo.current?.focus(),
      inserir: (inserido: string) => {
        setTexto(
          (atual) =>
            `${atual}${atual && !/\s$/.test(atual) ? ' ' : ''}${inserido} `,
        );
        requestAnimationFrame(() => {
          const elemento = campo.current;
          if (!elemento) return;
          elemento.focus();
          const fim = elemento.value.length;
          elemento.setSelectionRange(fim, fim);
        });
      },
    }),
    [adicionarArquivos],
  );

  // Abrir o canal ou responder: foca o campo (como o Discord, no computador).
  useEffect(() => {
    if (window.matchMedia?.('(pointer: fine)').matches) campo.current?.focus();
  }, []);
  useEffect(() => {
    if (resposta) campo.current?.focus();
  }, [resposta]);

  // Modo lento: contagem até poder enviar de novo.
  useEffect(() => {
    if (!esperaAte) return;
    const id = window.setInterval(() => {
      setAgora(Date.now());
      if (Date.now() >= esperaAte) setEsperaAte(null);
    }, 250);
    return () => window.clearInterval(id);
  }, [esperaAte]);
  const esperando = esperaAte !== null && esperaAte > agora;

  const grupos = useSugestoes(gatilho, canalId, guildId);
  const planas = grupos.flatMap((grupo) => grupo.itens);
  const sugestoesAbertas = !!gatilho && planas.length > 0;

  // Esc dispensa as sugestões até o gatilho mudar (outra letra, outro lugar).
  const dispensado = useRef<string | null>(null);
  const atualizarGatilho = (valor: string, cursor: number) => {
    const achado = acharGatilho(valor, cursor);
    const chave = achado
      ? `${achado.inicio}:${achado.tipo}${achado.termo}`
      : null;
    if (chave !== dispensado.current) dispensado.current = null;
    const novo = chave && chave === dispensado.current ? null : achado;
    setGatilho((atual) =>
      atual &&
      novo &&
      atual.tipo === novo.tipo &&
      atual.termo === novo.termo &&
      atual.inicio === novo.inicio
        ? atual
        : novo,
    );
    setSelecionada(0);
  };

  const escolher = (sugestao: Sugestao) => {
    if (!gatilho) return;
    if (sugestao.codigo)
      escolhidas.current.set(sugestao.inserir, sugestao.codigo);
    const resultado = substituir(texto, gatilho, sugestao.inserir);
    setTexto(resultado.texto);
    setGatilho(null);
    requestAnimationFrame(() => {
      campo.current?.focus();
      campo.current?.setSelectionRange(resultado.cursor, resultado.cursor);
    });
  };

  const inserirNoCursor = (inserido: string) => {
    const elemento = campo.current;
    const inicio = elemento?.selectionStart ?? texto.length;
    const fim = elemento?.selectionEnd ?? texto.length;
    const antes = texto.slice(0, inicio);
    const espaco = antes && !/\s$/.test(antes) ? ' ' : '';
    const novo = `${antes}${espaco}${inserido} ${texto.slice(fim)}`;
    const cursor = inicio + espaco.length + inserido.length + 1;
    setTexto(novo);
    requestAnimationFrame(() => {
      elemento?.focus();
      elemento?.setSelectionRange(cursor, cursor);
    });
  };

  // Envios em fila: a caixa limpa na hora e as mensagens saem na ordem em
  // que foram escritas (mesmo quando uma precisa esperar os emojis).
  const fila = useRef<Promise<void>>(Promise.resolve());
  const enviar = () => {
    if (!estado.canSend || esperando) return;
    const bruto = texto.trim();
    if (!bruto && !anexos.length) return;
    if (bruto.length > MAX_CARACTERES) {
      avisar(`A mensagem passa de ${MAX_CARACTERES} caracteres.`);
      return;
    }
    const arquivos = anexos.map((anexo) => ({
      name: anexo.spoiler
        ? `SPOILER_${anexo.arquivo.name}`
        : anexo.arquivo.name,
      data: anexo.arquivo as Blob,
    }));
    const respostaDe = resposta
      ? { messageId: resposta.mensagem.id, mention: resposta.mencionar }
      : null;
    const codigos = new Map(escolhidas.current);
    for (const anexo of anexos) {
      if (anexo.previa) URL.revokeObjectURL(anexo.previa);
    }
    setTexto('');
    setAnexos([]);
    setGatilho(null);
    escolhidas.current.clear();
    aoMudarResposta(null);
    cliente.stopTyping(canalId);
    void cliente.drafts.clear(canalId);
    if (estado.slowmodeSeconds > 0) {
      setEsperaAte(Date.now() + estado.slowmodeSeconds * 1000);
    }
    tocarSom('enviada');
    const precisaDeAtalhos = /(^|[^<\w]):[A-Za-z0-9_+-]{2,32}:/.test(bruto);
    fila.current = fila.current.then(async () => {
      const atalhos = precisaDeAtalhos
        ? mapaDeAtalhos(await carregarEmojis().catch(() => []))
        : undefined;
      const conteudo = paraEnvio(
        converter ? comEmoticons(bruto) : bruto,
        contextoDoCanal(cliente.store.getState(), canalId, codigos, atalhos),
      );
      cliente
        .sendMessage(canalId, {
          content: conteudo,
          files: arquivos,
          replyTo: respostaDe,
        })
        .catch((erro: unknown) => {
          // Recusada: fica na lista como "não enviada" (com tentar de novo).
          if (isChatApiError(erro) && erro.retryAfter) {
            setEsperaAte(Date.now() + erro.retryAfter * 1000);
          }
        });
    });
  };

  const aoTeclar = (evento: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (sugestoesAbertas) {
      if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
        evento.preventDefault();
        const passo = evento.key === 'ArrowDown' ? 1 : -1;
        setSelecionada(
          (atual) => (atual + passo + planas.length) % planas.length,
        );
        return;
      }
      if (evento.key === 'Enter' || evento.key === 'Tab') {
        evento.preventDefault();
        escolher(planas[Math.min(selecionada, planas.length - 1)]);
        return;
      }
      if (evento.key === 'Escape') {
        evento.preventDefault();
        dispensado.current = gatilho
          ? `${gatilho.inicio}:${gatilho.tipo}${gatilho.termo}`
          : null;
        setGatilho(null);
        return;
      }
    }
    // "Enter envia" (U7); desligado, Enter quebra a linha e Ctrl+Enter envia.
    const comCtrl = evento.ctrlKey || evento.metaKey;
    if (
      evento.key === 'Enter' &&
      !evento.shiftKey &&
      !evento.nativeEvent.isComposing &&
      (prefs.enterEnvia || comCtrl)
    ) {
      evento.preventDefault();
      enviar();
      return;
    }
    if (evento.key === 'Escape' && resposta) {
      evento.preventDefault();
      aoMudarResposta(null);
      return;
    }
    if (evento.key === 'ArrowUp' && !texto && !anexos.length) {
      evento.preventDefault();
      aoEditarUltima();
    }
  };

  if (!estado.canSend) {
    return (
      <SemPermissao
        motivo={estado.reason}
        castigoAte={castigoAte}
        guildId={guildId}
      />
    );
  }

  const placeholder =
    nomeDoCanal.texto ??
    (nomeDoCanal.tipo === 'dm'
      ? t('compositor.placeholderDm', { nome: nomeDoCanal.nome })
      : t('compositor.placeholder', { canal: nomeDoCanal.nome }));
  const restantes = MAX_CARACTERES - texto.length;

  return (
    <form
      ref={formulario}
      aria-label="Enviar mensagem"
      className="relative shrink-0 px-4"
      onSubmit={(evento) => {
        evento.preventDefault();
        enviar();
      }}
    >
      {sugestoesAbertas && gatilho && (
        <Sugestoes
          id={idSugestoes}
          gatilho={gatilho}
          grupos={grupos}
          selecionada={Math.min(selecionada, planas.length - 1)}
          aoEscolher={escolher}
          aoPassar={setSelecionada}
        />
      )}
      {resposta && (
        <BarraResposta
          resposta={resposta}
          guildId={guildId}
          aoAlternar={() =>
            aoMudarResposta({ ...resposta, mencionar: !resposta.mencionar })
          }
          aoCancelar={() => aoMudarResposta(null)}
        />
      )}
      {anexos.length > 0 && (
        <div
          role="group"
          aria-label={`Anexos, ${anexos.length} ${anexos.length === 1 ? 'arquivo' : 'arquivos'}`}
          className={`rolagem flex gap-5 overflow-x-auto border-b border-borda bg-campo px-4 pt-4 pb-2 ${
            resposta ? '' : 'rounded-t-[10px]'
          }`}
        >
          {anexos.map((anexo) => (
            <CartaoAnexo
              key={anexo.id}
              anexo={anexo}
              aoRemover={() => removerAnexo(anexo.id)}
              aoSpoiler={() =>
                setAnexos((atuais) =>
                  atuais.map((item) =>
                    item.id === anexo.id
                      ? { ...item, spoiler: !item.spoiler }
                      : item,
                  ),
                )
              }
            />
          ))}
        </div>
      )}
      <div
        className={`flex min-h-12 items-end gap-1 bg-campo px-2 ${
          resposta || anexos.length ? 'rounded-b-[10px]' : 'rounded-[10px]'
        }`}
      >
        <button
          type="button"
          aria-label="Anexar arquivo ou criar enquete"
          title="Anexar arquivo ou criar enquete"
          aria-haspopup="menu"
          aria-expanded={!!menuMais}
          onClick={(evento) => {
            const caixa = evento.currentTarget.getBoundingClientRect();
            setMenuMais(menuMais ? null : { x: caixa.left, y: caixa.top - 8 });
          }}
          className="mb-1.5 flex size-9 shrink-0 items-center justify-center rounded-full text-texto-2 hover:text-texto"
        >
          <Icone nome="mais" tamanho={22} />
        </button>
        {menuMais && (
          <Flutuante
            ancora={menuMais}
            aoFechar={() => setMenuMais(null)}
            rotulo="Anexar"
            papel="menu"
            abrir="cima"
          >
            <CaixaMenu largura={220}>
              <ItemMenu
                icone="enviar-arquivo"
                aoEscolher={() => {
                  setMenuMais(null);
                  seletorArquivo.current?.click();
                }}
              >
                Enviar arquivo
              </ItemMenu>
              {podeEnquete && (
                <ItemMenu
                  icone="enquete"
                  aoEscolher={() => {
                    setMenuMais(null);
                    abrirCamada({ tipo: 'criar-enquete', canalId });
                  }}
                >
                  Criar enquete
                </ItemMenu>
              )}
            </CaixaMenu>
          </Flutuante>
        )}
        <input
          ref={seletorArquivo}
          type="file"
          multiple
          hidden
          onChange={(evento) => {
            adicionarArquivos([...(evento.target.files ?? [])]);
            evento.target.value = '';
          }}
        />
        <label htmlFor={`${idSugestoes}-campo`} className="sr-only">
          {placeholder}
        </label>
        <textarea
          id={`${idSugestoes}-campo`}
          ref={campo}
          rows={1}
          value={texto}
          placeholder={placeholder}
          aria-autocomplete="list"
          aria-controls={sugestoesAbertas ? idSugestoes : undefined}
          aria-activedescendant={
            sugestoesAbertas ? `${idSugestoes}-${selecionada}` : undefined
          }
          onChange={(evento) => {
            setTexto(evento.target.value);
            atualizarGatilho(evento.target.value, evento.target.selectionStart);
            if (evento.target.value.trim() && prefs.mostrarDigitando)
              void cliente.startTyping(canalId);
          }}
          onSelect={(evento) =>
            atualizarGatilho(
              evento.currentTarget.value,
              evento.currentTarget.selectionStart,
            )
          }
          onBlur={() => setGatilho(null)}
          onKeyDown={aoTeclar}
          spellCheck={prefs.corretor}
          onPaste={(evento) => {
            const arquivos = [...evento.clipboardData.files];
            if (arquivos.length) {
              evento.preventDefault();
              adicionarArquivos(arquivos);
            }
          }}
          className="max-h-[50vh] min-w-0 grow resize-none border-0 bg-transparent py-[13px] text-[15px] leading-[1.45] text-texto outline-none placeholder:text-texto-3"
        />
        {restantes < 200 && (
          <span
            className={`mb-[15px] shrink-0 text-xs font-semibold ${restantes < 0 ? 'text-ocupado' : 'text-texto-3'}`}
          >
            {restantes}
          </span>
        )}
        <button
          ref={botaoEmoji}
          type="button"
          aria-label="Emojis"
          title="Emojis"
          onClick={(evento) => {
            const caixa = evento.currentTarget.getBoundingClientRect();
            setEmojiAberto(
              emojiAberto ? null : { x: caixa.right, y: caixa.top - 8 },
            );
          }}
          className="mb-1.5 flex size-9 shrink-0 items-center justify-center rounded-lg text-texto-2 hover:text-texto"
        >
          <Icone nome="sorriso" tamanho={22} />
        </button>
      </div>
      {esperando && (
        <div className="absolute right-6 -bottom-0.5 text-xs font-semibold text-texto-3">
          {t('compositor.modoLento', {
            duracao: formatDuration(estado.slowmodeSeconds),
          })}{' '}
          · faltam {Math.ceil(((esperaAte ?? 0) - agora) / 1000)} s
        </div>
      )}
      {emojiAberto && (
        <Flutuante
          ancora={emojiAberto}
          aoFechar={() => setEmojiAberto(null)}
          rotulo="Seletor de emoji"
          alinhar="fim"
          abrir="cima"
        >
          <SeletorEmoji
            canalId={canalId}
            aoEscolher={(emoji) => {
              setEmojiAberto(null);
              inserirNoCursor(emoji.id ? `:${emoji.name}:` : emoji.name);
            }}
            aoEscolherFigurinha={(figurinha) => {
              setEmojiAberto(null);
              cliente
                .sendMessage(canalId, {
                  content: '',
                  stickerIds: [figurinha.id],
                  replyTo: resposta
                    ? {
                        messageId: resposta.mensagem.id,
                        mention: resposta.mencionar,
                      }
                    : null,
                })
                .catch(() => undefined);
              aoMudarResposta(null);
            }}
          />
        </Flutuante>
      )}
    </form>
  );
}

function BarraResposta({
  resposta,
  guildId,
  aoAlternar,
  aoCancelar,
}: {
  resposta: Resposta;
  guildId: string | null;
  aoAlternar: () => void;
  aoCancelar: () => void;
}) {
  const nome = useNome(
    resposta.mensagem.author.id,
    guildId,
    resposta.mensagem.author,
  );
  const minha = useEstado(
    (estado) => estado.me?.id === resposta.mensagem.author.id,
  );
  return (
    <div className="flex h-9 items-center gap-2 rounded-t-[10px] bg-lateral pr-2 pl-3.5 text-sm text-texto-2">
      <Icone nome="responder" tamanho={16} />
      <span className="min-w-0 grow truncate">
        Respondendo a{' '}
        <strong className="font-semibold text-texto">{nome}</strong>
      </span>
      {!minha && (
        <button
          type="button"
          aria-pressed={resposta.mencionar}
          aria-label={`Mencionar ${nome} na resposta`}
          title={
            resposta.mencionar
              ? 'A pessoa será avisada'
              : 'A pessoa não será avisada'
          }
          onClick={aoAlternar}
          className={`flex h-[26px] items-center gap-1 rounded-md px-2 text-xs font-bold tracking-[.04em] ${
            resposta.mencionar ? 'text-destaque' : 'text-texto-3'
          }`}
        >
          <Icone nome="arroba" tamanho={14} />
          {resposta.mencionar ? 'LIGADO' : 'DESLIGADO'}
        </button>
      )}
      <span className="h-[18px] w-px shrink-0 bg-borda" />
      <button
        type="button"
        aria-label="Cancelar resposta"
        onClick={aoCancelar}
        className="flex size-6 items-center justify-center rounded-full bg-ativo text-texto-2 hover:text-texto"
      >
        <Icone nome="x" tamanho={14} />
      </button>
    </div>
  );
}

function CartaoAnexo({
  anexo,
  aoRemover,
  aoSpoiler,
}: {
  anexo: Anexo;
  aoRemover: () => void;
  aoSpoiler: () => void;
}) {
  const extensao = anexo.arquivo.name.split('.').pop()?.toUpperCase() ?? '';
  return (
    <div className="relative flex h-[180px] w-[200px] shrink-0 flex-col gap-1.5 rounded-lg border border-borda bg-chat p-2">
      <div className="absolute -top-2.5 -right-2.5 flex gap-0.5 rounded-lg border border-borda bg-elevado p-0.5 shadow-[0_4px_12px_rgba(0,0,0,.35)]">
        <button
          type="button"
          aria-label={`Remover anexo ${anexo.arquivo.name}`}
          onClick={aoRemover}
          className="flex size-7 items-center justify-center rounded-md text-ocupado hover:bg-ativo"
        >
          <Icone nome="lixeira" tamanho={18} />
        </button>
      </div>
      <div className="flex h-[116px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-elevado">
        {anexo.previa ? (
          <img
            src={anexo.previa}
            alt=""
            className={`h-full w-full object-cover ${anexo.spoiler ? 'blur-xl' : ''}`}
          />
        ) : (
          <span className="flex flex-col items-center gap-1.5 text-texto-3">
            <Icone nome="arquivo" tamanho={32} />
            <span className="text-[11px] font-bold tracking-[.04em]">
              {extensao}
            </span>
          </span>
        )}
      </div>
      <span
        className="truncate text-[13px] font-medium"
        title={anexo.arquivo.name}
      >
        {anexo.arquivo.name}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={anexo.spoiler}
        onClick={aoSpoiler}
        className="flex h-4 items-center gap-1.5 self-start text-xs text-texto-3"
      >
        <span
          aria-hidden="true"
          className={`relative h-4 w-[26px] shrink-0 rounded-lg ${anexo.spoiler ? 'bg-destaque' : 'bg-trilho'}`}
        >
          <span
            className={`absolute top-0.5 size-3 rounded-full ${
              anexo.spoiler ? 'left-3 bg-destaque-texto' : 'left-0.5 bg-texto-2'
            }`}
          />
        </span>
        Spoiler
      </button>
    </div>
  );
}

/**
 * Quando não dá para escrever (C10, C43): o motivo no lugar do campo. Quem
 * ainda não aceitou as regras abre a A13 daqui (a barra do Discord).
 */
function SemPermissao({
  motivo,
  castigoAte,
  guildId,
}: {
  motivo:
    'no-permission' | 'timeout' | 'pending' | 'locked-thread' | 'not-writable';
  castigoAte: string | null;
  guildId: string | null;
}) {
  const texto = {
    'no-permission': t('compositor.semPermissao'),
    timeout: t('compositor.castigo', {
      quando: castigoAte
        ? formatMessageTime(castigoAte).replace(/^Hoje às/, 'hoje às')
        : 'daqui a pouco',
    }),
    pending: t('compositor.pendente'),
    'locked-thread': t('compositor.threadTravada'),
    'not-writable': 'Não dá para enviar mensagens aqui.',
  }[motivo];
  return (
    <div className="shrink-0 px-4">
      <div
        role="status"
        className="flex h-12 items-center gap-2.5 rounded-[10px] border border-borda bg-lateral pr-2 pl-3.5"
      >
        <Icone
          nome={motivo === 'timeout' ? 'relogio' : 'cadeado'}
          tamanho={18}
          className="shrink-0 text-texto-3"
        />
        <p className="m-0 min-w-0 grow truncate text-sm text-texto-3">
          {texto}
        </p>
        {motivo === 'pending' && guildId && (
          <button
            type="button"
            onClick={() => abrirCamada({ tipo: 'regras', guildId })}
            className="h-8 shrink-0 rounded-md bg-destaque px-3 text-[13px] font-semibold text-destaque-texto hover:brightness-105"
          >
            {t('compositor.verRegras')}
          </button>
        )}
      </div>
    </div>
  );
}
